import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Tabs, Tab, Table, TableHead, TableRow,
  TableCell, TableBody, Button, Chip, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, TextField, IconButton, Tooltip, Alert,
  Stack, Divider,
} from '@mui/material';
import {
  Refresh, Sync, Delete, Add, CheckCircle, Cancel, Speed, Router as RouterIcon,
  Block, NetworkCheck, Assessment, MonitorHeart, ReportProblem, Warning,
  Timeline, PlayArrow, Stop, FiberManualRecord,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuthStore, financialHidden } from '../../store/auth';

const num = (v: any, d = 1) => (v === null || v === undefined ? '-' : Number(v).toFixed(d));

function StatCard({ label, value, color }: { label: string; value: any; color?: string }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: color || 'text.primary' }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export const SbcControlPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [overview, setOverview] = useState<any>({});
  const [gateways, setGateways] = useState<any[]>([]);
  const [routes, setRoutes] = useState<any>({ rules: [] });
  const [prefix, setPrefix] = useState('');
  const [blocklist, setBlocklist] = useState<any[]>([]);

  const [blockDlg, setBlockDlg] = useState(false);
  const [blockForm, setBlockForm] = useState<any>({ block_type: 'country', pattern: '', description: '' });

  // ── Monitoring / Alerts ──
  const [alerts, setAlerts] = useState<any>(null);
  const [alertHours, setAlertHours] = useState(24);
  const [alertLoading, setAlertLoading] = useState(false);
  const loadAlerts = useCallback(async (hrs?: number) => {
    setAlertLoading(true);
    try { const r = await api.get('/sbc/alerts', { params: { hours: hrs ?? alertHours } }); setAlerts(r.data); }
    catch { setAlerts(null); } finally { setAlertLoading(false); }
  }, [alertHours]);

  // ── SIP Capture (Homer-style, on-demand) ──
  const [cap, setCap] = useState<any>({ capturing: false, files: 0 });
  const [capData, setCapData] = useState<any>(null);
  const [capBusy, setCapBusy] = useState(false);
  const capStatus = useCallback(async () => { try { const r = await api.get('/sbc/capture/status'); setCap(r.data); } catch {} }, []);
  const capMessages = useCallback(async () => { setCapBusy(true); try { const r = await api.get('/sbc/capture/messages'); setCapData(r.data); setCap((c: any) => ({ ...c, capturing: r.data.capturing })); } catch {} finally { setCapBusy(false); } }, []);
  const capToggle = async (start: boolean) => {
    setCapBusy(true);
    try { const r = await api.post(`/sbc/capture/${start ? 'start' : 'stop'}`); setCap(r.data); if (!start) capMessages(); }
    catch (e: any) { flash('error', 'Capture ' + (start ? 'start' : 'stop') + ' failed'); } finally { setCapBusy(false); }
  };
  const capClear = async () => { setCapBusy(true); try { await api.post('/sbc/capture/clear'); setCapData(null); capStatus(); } finally { setCapBusy(false); } };

  const flash = (type: 'success' | 'error', text: string) => {
    setMsg({ type, text }); setTimeout(() => setMsg(null), 5000);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [o, g, b] = await Promise.all([
        api.get('/sbc/overview'), api.get('/sbc/gateways'), api.get('/sbc/blocklist'),
      ]);
      setOverview(o.data); setGateways(g.data); setBlocklist(b.data);
    } catch (e: any) {
      flash('error', e?.response?.data?.message || 'Failed to load SBC data');
    } finally { setLoading(false); }
  }, []);

  const loadRoutes = useCallback(async (p: string) => {
    try {
      const r = await api.get('/sbc/routes', { params: p ? { prefix: p } : {} });
      setRoutes(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadAll(); loadRoutes(''); }, [loadAll, loadRoutes]);

  const syncRouting = async () => {
    setLoading(true);
    try {
      const r = await api.post('/sbc/sync-routing');
      flash('success', `${r.data.message}${r.data.reloaded ? ' — Kamailio reloaded' : ''}`);
      await loadAll();
    } catch (e: any) { flash('error', e?.response?.data?.message || 'Sync failed'); }
    finally { setLoading(false); }
  };

  const refreshQuality = async () => {
    setLoading(true);
    try {
      const r = await api.post('/sbc/quality/refresh');
      flash('success', r.data.message || 'Quality refreshed');
      await loadAll();
    } catch (e: any) { flash('error', 'Quality refresh failed'); }
    finally { setLoading(false); }
  };

  const addBlock = async () => {
    try {
      await api.post('/sbc/blocklist', blockForm);
      setBlockDlg(false); setBlockForm({ block_type: 'country', pattern: '', description: '' });
      flash('success', 'Block rule added'); await loadAll();
    } catch (e: any) { flash('error', e?.response?.data?.message || 'Add failed'); }
  };

  const delBlock = async (id: number) => {
    try { await api.delete(`/sbc/blocklist/${id}`); flash('success', 'Removed'); await loadAll(); }
    catch { flash('error', 'Delete failed'); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>SBC/Trunk Control Panel</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadAll} disabled={loading}>Refresh</Button>
          <Button variant="outlined" startIcon={<Assessment />} onClick={refreshQuality} disabled={loading}>Refresh Quality</Button>
          <Button variant="contained" startIcon={<Sync />} onClick={syncRouting} disabled={loading}>Sync Routing to SBC</Button>
        </Stack>
      </Stack>

      {msg && <Alert severity={msg.type} sx={{ mb: 2 }}>{msg.text}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}><StatCard label="Active Carriers" value={overview.active_carriers ?? '-'} /></Grid>
        <Grid item xs={6} md={2}><StatCard label="LCR Gateways" value={overview.lcr_gateways ?? '-'} color="#1565c0" /></Grid>
        <Grid item xs={6} md={2}><StatCard label="LCR Rules" value={overview.lcr_rules ?? '-'} color="#1565c0" /></Grid>
        <Grid item xs={6} md={2}><StatCard label="Quality-Blocked" value={overview.quality_blocked ?? '-'} color={overview.quality_blocked ? '#c62828' : undefined} /></Grid>
        <Grid item xs={6} md={2}><StatCard label="Block Rules" value={overview.block_rules ?? '-'} color="#c62828" /></Grid>
        <Grid item xs={6} md={2}><StatCard label="Active Calls" value={overview.active_calls ?? '-'} color="#2e7d32" /></Grid>
      </Grid>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<NetworkCheck />} iconPosition="start" label="Gateways & Quality" />
          <Tab icon={<RouterIcon />} iconPosition="start" label="LCR Routes" />
          <Tab icon={<Block />} iconPosition="start" label="Blocklist" />
          <Tab icon={<MonitorHeart />} iconPosition="start" label="Monitoring / Alerts" onClick={() => loadAlerts()} />
          <Tab icon={<Timeline />} iconPosition="start" label="SIP Capture" onClick={() => capStatus()} />
        </Tabs>

        {loading && <CircularProgress sx={{ m: 3 }} />}

        {/* ---- Gateways & Quality ---- */}
        {tab === 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Carrier</TableCell><TableCell>Host / Transport</TableCell>
                  <TableCell>Status</TableCell><TableCell>Reachable</TableCell>
                  <TableCell align="right">ASR %</TableCell><TableCell align="right">ACD s</TableCell>
                  <TableCell align="right">PDD s</TableCell><TableCell align="right">Channels</TableCell>
                  <TableCell align="right">Max CPS</TableCell><TableCell>In LCR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gateways.map((g) => (
                  <TableRow key={g.id} hover>
                    <TableCell><b>{g.name}</b>{g.gateway_name ? <span style={{ display: 'block', fontSize: 11, color: '#888' }}>{g.gateway_name}{g.is_primary ? ' • primary' : ''}</span> : null}</TableCell>
                    <TableCell>{g.host}:{g.port} <Chip size="small" label={g.protocol} sx={{ ml: 0.5 }} /></TableCell>
                    <TableCell>
                      <Chip size="small" color={g.status === 'active' ? 'success' : 'default'} label={g.status} />
                      {!g.quality_ok && <Chip size="small" color="error" label="quality" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={g.reachable
                        ? (g.reach_method === 'ping' ? 'Reachable via network/ping — carrier does not answer SIP OPTIONS' : 'Reachable (answers SIP OPTIONS)')
                        : 'Unreachable (no SIP OPTIONS reply and host not pingable)'}>
                        <span>{g.reachable
                          ? <CheckCircle color={g.reach_method === 'ping' ? 'warning' : 'success'} fontSize="small" />
                          : <Cancel color="disabled" fontSize="small" />}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{num(g.asr, 1)}</TableCell>
                    <TableCell align="right">{num(g.acd, 0)}</TableCell>
                    <TableCell align="right">{num(g.pdd, 1)}</TableCell>
                    <TableCell align="right">{g.current_channels}/{g.max_channels || '∞'}</TableCell>
                    <TableCell align="right">{g.max_cps || '∞'}</TableCell>
                    <TableCell>{g.in_lcr ? <Chip size="small" color="primary" label="yes" /> : <Chip size="small" label="no" />}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* ---- LCR Routes ---- */}
        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
              <TextField size="small" label="Search dialled prefix" value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') loadRoutes(prefix); }} />
              <Button variant="outlined" startIcon={<Speed />} onClick={() => loadRoutes(prefix)}>Search</Button>
              <Typography variant="body2" color="text.secondary">
                {routes.total_rules} rules in {routes.groups} group(s). Gateway list is cheapest-first (LCR) with failover.
              </Typography>
            </Stack>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Prefix</TableCell><TableCell>Gateway IDs (LCR order)</TableCell><TableCell>Description</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {(routes.rules || []).map((r: any, i: number) => (
                  <TableRow key={i} hover>
                    <TableCell><b>{r.prefix}</b></TableCell>
                    <TableCell>{r.gwlist}</TableCell>
                    <TableCell>{r.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* ---- Blocklist ---- */}
        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <Button variant="contained" startIcon={<Add />} sx={{ mb: 2 }} onClick={() => setBlockDlg(true)}>Add Block Rule</Button>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Type</TableCell><TableCell>Pattern</TableCell><TableCell>Scope</TableCell>
                <TableCell>Description</TableCell><TableCell>Active</TableCell><TableCell></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {blocklist.map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell><Chip size="small" label={b.block_type} /></TableCell>
                    <TableCell><b>{b.pattern}</b></TableCell>
                    <TableCell>{b.tenant_name || 'Global'}</TableCell>
                    <TableCell>{b.description}</TableCell>
                    <TableCell>{b.active ? <CheckCircle color="success" fontSize="small" /> : <Cancel color="disabled" fontSize="small" />}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove"><IconButton size="small" onClick={() => delBlock(b.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* ---- Monitoring / Alerts ---- */}
        {tab === 3 && (
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TextField select size="small" label="Window" value={alertHours} sx={{ width: 140 }}
                onChange={(e) => { const v = Number(e.target.value); setAlertHours(v); loadAlerts(v); }}>
                {[1, 6, 24, 72, 168].map((h) => <MenuItem key={h} value={h}>{h < 24 ? `${h}h` : `${h / 24}d`}</MenuItem>)}
              </TextField>
              <Button variant="outlined" startIcon={alertLoading ? <CircularProgress size={16} /> : <Refresh />} onClick={() => loadAlerts()} disabled={alertLoading}>Refresh</Button>
              {alerts?.counts && (
                <Stack direction="row" spacing={1}>
                  <Chip color="error" size="small" label={`${alerts.counts.call_failures} call failures`} />
                  <Chip color="warning" size="small" label={`${alerts.counts.trunk} trunk`} />
                  <Chip size="small" label={`${alerts.counts.routing} failover`} />
                  <Chip color="secondary" size="small" label={`${alerts.counts.fraud} fraud`} />
                  <Chip size="small" label={`${alerts.counts.balance} balance`} />
                </Stack>
              )}
            </Stack>

            {!alerts ? <Alert severity="info">Click Refresh to load alerts for the selected window.</Alert> : (
              <Stack spacing={3}>
                {/* Call failures */}
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom><ReportProblem fontSize="small" color="error" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Call Failures — why calls failed</Typography>
                  {alerts.call_failures.length === 0 ? <Alert severity="success">No failed calls in this window.</Alert> : (
                    <Table size="small"><TableHead><TableRow>
                      <TableCell>Time</TableCell><TableCell>Dir</TableCell><TableCell>From</TableCell><TableCell>To</TableCell>
                      <TableCell>Carrier</TableCell><TableCell>Code</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
                      <TableBody>{alerts.call_failures.map((c: any, i: number) => (
                        <TableRow key={i} hover>
                          <TableCell>{c.start_time ? new Date(c.start_time).toLocaleString() : '-'}</TableCell>
                          <TableCell>{c.direction || '-'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{c.src_number}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{c.dst_number}</TableCell>
                          <TableCell>{c.carrier_name || '-'}</TableCell>
                          <TableCell><Chip size="small" color="error" variant="outlined" label={c.hangup_code || '-'} /></TableCell>
                          <TableCell>{c.reason}</TableCell>
                        </TableRow>))}</TableBody></Table>
                  )}
                </Box>
                {/* Trunk alerts */}
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom><Warning fontSize="small" color="warning" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Trunk / Gateway Alerts</Typography>
                  {alerts.trunk_alerts.length === 0 ? <Alert severity="success">All gateways healthy.</Alert> : (
                    <Table size="small"><TableHead><TableRow>
                      <TableCell>Carrier</TableCell><TableCell>Gateway</TableCell><TableCell>Host</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
                      <TableBody>{alerts.trunk_alerts.map((t: any, i: number) => (
                        <TableRow key={i} hover>
                          <TableCell><b>{t.carrier}</b></TableCell><TableCell>{t.gateway}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{t.host}</TableCell><TableCell>{t.reason}</TableCell>
                        </TableRow>))}</TableBody></Table>
                  )}
                </Box>
                {/* Routing / failover */}
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom><RouterIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Routing / Failover Events</Typography>
                  {alerts.routing_events.length === 0 ? <Alert severity="success">No routing failover events.</Alert> : (
                    <Table size="small"><TableHead><TableRow>
                      <TableCell>Time</TableCell><TableCell>Prefix</TableCell><TableCell>Primary</TableCell><TableCell>Failover</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
                      <TableBody>{alerts.routing_events.map((r: any, i: number) => (
                        <TableRow key={i} hover>
                          <TableCell>{r.created_at ? new Date(r.created_at).toLocaleString() : '-'}</TableCell>
                          <TableCell>{r.destination_prefix} {r.destination_name ? `(${r.destination_name})` : ''}</TableCell>
                          <TableCell>{r.primary_carrier_name || '-'}</TableCell><TableCell>{r.failover_carrier_name || '-'}</TableCell>
                          <TableCell>{r.reason || r.status}</TableCell>
                        </TableRow>))}</TableBody></Table>
                  )}
                </Box>
                {/* Fraud + Balance */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Fraud Alerts</Typography>
                    {alerts.fraud_alerts.length === 0 ? <Alert severity="success">No open fraud alerts.</Alert> : alerts.fraud_alerts.map((f: any, i: number) => (
                      <Alert key={i} severity={f.severity === 'high' || f.severity === 'critical' ? 'error' : 'warning'} sx={{ mb: 1 }}>
                        <b>{f.alert_type}</b> — {f.description}
                      </Alert>))}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Balance Alerts (calls will 402)</Typography>
                    {alerts.balance_alerts.length === 0 ? <Alert severity="success">All prepaid customers have balance.</Alert> : (
                      <Table size="small"><TableHead><TableRow><TableCell>Customer</TableCell><TableCell align="right">{financialHidden(useAuthStore.getState().user?.role) ? 'Status' : 'Balance'}</TableCell></TableRow></TableHead>
                        <TableBody>{alerts.balance_alerts.map((b: any, i: number) => (
                          <TableRow key={i}><TableCell>{b.name}</TableCell><TableCell align="right" style={{ color: '#c62828' }}>{financialHidden(useAuthStore.getState().user?.role) ? 'Out of balance' : `${b.currency} ${Number(b.balance).toFixed(2)}`}</TableCell></TableRow>))}</TableBody></Table>
                    )}
                  </Grid>
                </Grid>
              </Stack>
            )}
          </Box>
        )}
        {/* ---- SIP Capture (Homer-style, on-demand) ---- */}
        {tab === 4 && (
          <Box sx={{ p: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              On-demand SIP capture — captures <b>decrypted</b> SIP (incl. the TLS/BT leg). It adds load and writes to disk,
              so <b>only run it while diagnosing</b>, then Stop. It does not run automatically.
            </Alert>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              {cap.capturing
                ? <Button variant="contained" color="error" startIcon={<Stop />} onClick={() => capToggle(false)} disabled={capBusy}>Stop Capture</Button>
                : <Button variant="contained" color="success" startIcon={<PlayArrow />} onClick={() => capToggle(true)} disabled={capBusy}>Start Capture</Button>}
              <Button variant="outlined" startIcon={capBusy ? <CircularProgress size={16} /> : <Refresh />} onClick={capMessages} disabled={capBusy}>Load Messages</Button>
              <Button variant="outlined" color="inherit" startIcon={<Delete />} onClick={capClear} disabled={capBusy}>Clear</Button>
              <Chip icon={<FiberManualRecord sx={{ color: cap.capturing ? '#c62828' : '#9e9e9e' }} />}
                label={cap.capturing ? 'CAPTURING' : 'stopped'} variant="outlined" />
              <Chip size="small" label={`${cap.files ?? 0} capture file(s)`} />
              {capData && <Chip size="small" color="primary" label={`${capData.total_messages} messages · ${capData.calls.length} calls`} />}
            </Stack>

            {!capData ? <Alert severity="info">Start capture, place/receive a call, then Load Messages to see the SIP flow.</Alert>
            : capData.calls.length === 0 ? <Alert severity="info">No SIP messages captured yet. Start capture, run a call, then Load Messages.</Alert>
            : (
              <Stack spacing={2}>
                {capData.calls.map((call: any) => (
                  <Card key={call.call_id} variant="outlined">
                    <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>Call-ID: {call.call_id}</Typography>
                      <Chip size="small" label={`${call.messages.length} msgs`} />
                    </Box>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell sx={{ width: 110 }}>Time</TableCell>
                        <TableCell>Source</TableCell><TableCell sx={{ width: 30 }}></TableCell><TableCell>Destination</TableCell>
                        <TableCell>SIP Message</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {call.messages.map((m: any, i: number) => {
                          const fl = m.first_line || '';
                          const code = (fl.match(/SIP\/2\.0 (\d{3})/) || [])[1];
                          const color = code ? (code[0] === '2' ? 'success.main' : (code[0] === '4' || code[0] === '5' || code[0] === '6') ? 'error.main' : 'text.secondary')
                            : (/^INVITE/.test(fl) ? 'primary.main' : /^BYE|^CANCEL/.test(fl) ? 'warning.main' : 'text.primary');
                          const t = new Date(m.time * 1000);
                          return (
                            <TableRow key={i} hover>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{t.toLocaleTimeString()}.{String(t.getMilliseconds()).padStart(3, '0')}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{m.src}</TableCell>
                              <TableCell>{m.dir === 'rcv' ? '→' : '←'}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{m.dst}</TableCell>
                              <TableCell sx={{ color, fontWeight: 600, fontSize: 12 }}>{fl} <Typography component="span" variant="caption" color="text.secondary">({m.cseq})</Typography></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Card>

      <Dialog open={blockDlg} onClose={() => setBlockDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Block Rule</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Block Type" value={blockForm.block_type}
              onChange={(e) => setBlockForm({ ...blockForm, block_type: e.target.value })}>
              <MenuItem value="country">Country code (destination)</MenuItem>
              <MenuItem value="dst">Destination number / prefix</MenuItem>
              <MenuItem value="cli">Caller ID (CLI) / prefix</MenuItem>
            </TextField>
            <TextField label="Pattern (bare digits, prefix match)" value={blockForm.pattern}
              onChange={(e) => setBlockForm({ ...blockForm, pattern: e.target.value })}
              helperText="e.g. 93 for Afghanistan, 447 for UK mobile, 1900 for premium" />
            <TextField label="Description" value={blockForm.description}
              onChange={(e) => setBlockForm({ ...blockForm, description: e.target.value })} />
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Applies globally (all customers) and takes effect immediately — Kamailio reads the blocklist live.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={addBlock} disabled={!blockForm.pattern}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SbcControlPage;
